import { useMemo, useState } from "react";

import { usePostHog } from "posthog-js/react";

import { RequirementsAppliedSnackbar } from "./RequirementsAppliedSnackbar";
import { useSubmitQuoteCommandLink } from "./useSubmitQuoteCommandLink";
import { useUnauthenticatedQuoteFormState } from "./useUnauthenticatedQuoteFormState";
import {
  buildSingleShipmentQuoteParams,
  toQuoteFormShipment,
  validateSingleShipmentQuote,
} from "./utils";
import { ClientAppEvent } from "../../../../constants";
import { SubmitQuoteForm } from "../../../Carrier/components/SubmitQuoteForm/SubmitQuoteForm";

import type { SubmitQuotePreviewShipment } from "./types";
import type { SubmitQuoteFormState } from "../../../Carrier/components/SubmitQuoteForm/types";

const INITIAL_STATE: SubmitQuoteFormState = {
  equipmentType: "",
  pickupStart: "",
  pickupStartTime: "",
  deliveryEta: "",
  deliveryEtaTime: "",
  currency: "CAD",
  price: null,
  notes: "",
  trackingLinkOffered: false,
};

interface UnauthenticatedSubmitQuoteFormContainerProps {
  shipment: SubmitQuotePreviewShipment;
  token: string;
  onQuoteSubmitted: VoidFunction;
  onRecoverableFailure: (outcomeCode: string | null) => void;
  // A stop the carrier cannot correct here, discovered on submit.
  onBlockedFailure: (blockedReasonCode: string) => void;
  // A recoverable rejection the carrier can correct without leaving the page.
  submissionError: string | null;
  // Rendered beside Submit: the landing's "No truck available" control.
  secondaryAction?: React.ReactNode;
}

/**
 * Drives the same presentational SubmitQuoteForm the app uses, sourcing its props
 * from the command-link preview instead of authenticated hooks and submitting
 * through the signed token. Handles SINGLE and QUOTES_ONLY; the form itself
 * branches on the shipment type
 * (docs/engineering/design/carrier_submit_quote_command_link_design.md D2).
 *
 * The auth-only concerns of the in-app container drop out here: the preview
 * already established the carrier has no live quote (so `canSubmitQuote` is
 * always true at this point) and there is nothing to refetch, and the
 * booking-limit, subscription-advisory and page-refresh dialogs all need a
 * logged-in session. The landing renders its own success screen instead.
 */
export function UnauthenticatedSubmitQuoteFormContainer(
  props: UnauthenticatedSubmitQuoteFormContainerProps,
) {
  const {
    shipment,
    token,
    onQuoteSubmitted,
    onRecoverableFailure,
    onBlockedFailure,
    submissionError,
    secondaryAction,
  } = props;
  const posthog = usePostHog();
  const [validationError, setValidationError] = useState<string | null>(null);
  const {
    formState,
    onValueChanged,
    onPopulateShipmentRequirements,
    snackbarOpened,
    onSnackbarClose,
    carrierTeammatesOpened,
    closeCarrierTeammates,
    openCarrierTeammates,
  } = useUnauthenticatedQuoteFormState({
    shipment,
    initialState: INITIAL_STATE,
  });

  const params = useMemo(
    () => buildSingleShipmentQuoteParams(formState, shipment),
    [formState, shipment],
  );

  const { submitQuote, loading, error } = useSubmitQuoteCommandLink({
    token,
    shipmentId: shipment.info.id,
    quoteType: shipment.info.type === "QUOTES_ONLY" ? "quotes_only" : "single",
    params,
    onSubmitted: onQuoteSubmitted,
    onRecoverableFailure,
    onBlockedFailure,
  });

  const onSubmit = () => {
    const formError = validateSingleShipmentQuote(
      formState,
      shipment.info.type,
    );
    setValidationError(formError);
    if (formError != null) {
      return;
    }

    posthog?.capture(ClientAppEvent.QUOTE_COMMAND_LINK_SUBMIT_CLICKED, {
      shipment_id: shipment.info.id,
      shipment_type: shipment.info.type,
    });
    submitQuote();
  };

  return (
    <>
      <SubmitQuoteForm
        secondaryAction={secondaryAction}
        onValueChanged={onValueChanged}
        enableSubmit
        onSubmit={onSubmit}
        error={validationError ?? error ?? submissionError}
        loading={loading}
        quoteSubmitted={false}
        canSubmitQuote
        submitBlockReason={null}
        formState={formState}
        quoteRefetchLoading={false}
        onPopulateShipmentRequirements={onPopulateShipmentRequirements}
        shipment={toQuoteFormShipment(shipment)}
        carrierTeammatesOpened={carrierTeammatesOpened}
        closeCarrierTeammates={closeCarrierTeammates}
        openCarrierTeammates={openCarrierTeammates}
      />
      <RequirementsAppliedSnackbar
        open={snackbarOpened}
        onClose={onSnackbarClose}
      />
    </>
  );
}
